import { FormSectionHandler } from '../entity.handler';
import { CompanyDataRetentionSettings } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { ComboField, NumberField } from '../utils';

interface EditorValues {
    source?: string;
    retainFor?: number;
    retainForUnit?: 'days' | 'months' | 'years';
}

interface LimitedCompanyResponse {
    id?: string;
    retention?: CompanyDataRetentionSettings;
}

export class RetentionSectionHandler<T extends LimitedCompanyResponse> implements FormSectionHandler<T, EditorValues> {

    type = 'form';
    icon = 'database';
    bulk = false;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    actions = [];
    warning = '';
    originalValues: EditorValues;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public entityType: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.SHARED.RETENTION.${key}`, values);
    }

    applyUpdate(entity: T, payload: any): Promise<any> {
        switch (this.entityType) {
            case 'client': return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload), { _cacheKey: `getClient/${entity.id}` });
            case 'vendor': return this.admin.applyUpdate(api => api.accounts.updateVendor(entity.id, payload), { _cacheKey: `getVendor/${entity.id}` });
            case 'distributor': return this.admin.applyUpdate(api => api.accounts.updateDistributor(entity.id, payload), { _cacheKey: `getDistributor/${entity.id}` });
            case 'companygroup': return this.admin.applyUpdate(api => api.accounts.updateCompanyGroup(entity.id, payload), { _cacheKey: `getCompanyGroup/${entity.id}` });
            default:
                throw new Error(`Update for '${this.entityType}' not supported`);
        }
    }

    async mapEntityToEditor(entity: LimitedCompanyResponse): Promise<EditorValues> {
        this.warning = '';
        if (entity.retention) {
            this.warning = this.translate(`RETENTION_MESSAGE`, { date: this.admin.units.format(entity.retention.horizonDate, 'date').format });
        }
        this.originalValues = {
            source: entity.retention?.source?.id === entity.id ? 'custom' : 'owner',
            retainFor: entity.retention?.retainFor,
            retainForUnit: entity.retention?.retainForUnit,
        };
        return { ...this.originalValues };
    }

    async mapEditorToEntity(_entity: LimitedCompanyResponse, values: EditorValues): Promise<LimitedCompanyResponse> {
        if (values?.source === 'owner') {
            return { retention: null };
        }
        return {
            retention: {
                retainFor: values.retainFor || this.originalValues.retainFor,
                retainForUnit: values.retainForUnit || this.originalValues.retainForUnit,
            },
        };
    }

    async getFields(entity: LimitedCompanyResponse): Promise<FormBuilderField[]> {
        const units = ['months', 'days', 'years'].map(x => ({ key: x, value: this.translate(`FIELDS.UNITS.${x.toUpperCase()}`) }));


        let ownerType = entity?.retention?.source?.type || 'owner';
        if (entity?.retention?.source?.id === entity.id) {
            ownerType = 'owner';
        }
        const ownerName = this.translate(`FIELDS.SOURCE_VALUES.${ownerType.toUpperCase()}`);

        const fields = [ComboField('source', this.translate('FIELDS.RETENTION_SOURCE'), true, [
            {
                key: 'owner', value: ownerName, fields: [
                    { ...NumberField('retainFor', this.translate('FIELDS.RETAIN_FOR'), true, 1, 1000, ''), readonly: true },
                    { ...ComboField('retainForUnit', this.translate('FIELDS.RETAIN_FOR_UNIT'), true, units), readonly: true },
                ],
            },
            {
                key: 'custom', value: this.translate('FIELDS.SOURCE_VALUES.CUSTOM'), fields: [
                    NumberField('retainFor', this.translate('FIELDS.RETAIN_FOR'), true, 1, 1000, ''),
                    ComboField('retainForUnit', this.translate('FIELDS.RETAIN_FOR_UNIT'), true, units),
                ],
            },
        ])];



        return fields;
    }


}
